import { useTeam } from '../contexts/TeamContext';
import { useNavigate } from 'react-router-dom';

interface LimitWarningProps {
  type: 'bot' | 'seat';
}

export default function LimitWarning({ type }: LimitWarningProps) {
  const { currentTeam } = useTeam();
  const navigate = useNavigate();

  if (!currentTeam) return null;

  const license = currentTeam.license;
  const current = type === 'bot' ? license.current_agents : license.current_seats;
  const limit = type === 'bot' ? license.agent_limit : license.seat_limit;
  const isOwner = currentTeam.role === 'owner';

  // Calculate percentage used
  const percentage = limit > 0 ? (current / limit) * 100 : 0;

  // Don't show warning if below 80%
  if (percentage < 80) return null;

  // Determine warning level
  const atLimit = current >= limit;
  const nearLimit = percentage >= 80 && !atLimit;

  const label = type === 'bot' ? 'AI agent instances' : 'seats';
  const warningClass = atLimit
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
  const iconClass = atLimit
    ? 'text-red-600 dark:text-red-400'
    : 'text-yellow-600 dark:text-yellow-400';
  const textClass = atLimit
    ? 'text-red-900 dark:text-red-300'
    : 'text-yellow-900 dark:text-yellow-300';

  const message = atLimit
    ? `You've reached your ${label} limit (${current}/${limit})`
    : `You're approaching your ${label} limit (${current}/${limit})`;

  return (
    <div className={`border rounded-xl p-4 mb-6 ${warningClass}`}>
      <div className="flex items-start gap-3">
        <svg
          className={`w-6 h-6 flex-shrink-0 mt-0.5 ${iconClass}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <div className="flex-1">
          <p className={`font-semibold ${textClass}`}>{message}</p>
          {atLimit && (
            <p className={`text-sm mt-1 ${textClass}`}>
              {type === 'bot'
                ? 'Remove existing AI agent instances or upgrade your plan to add more.'
                : 'Remove team members or upgrade your plan to invite more.'}
            </p>
          )}
          {nearLimit && (
            <p className={`text-sm mt-1 ${textClass}`}>
              Consider upgrading your plan to avoid disruptions.
            </p>
          )}
        </div>
        {isOwner && (
          <button
            onClick={() => navigate('/billing')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              atLimit
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-yellow-600 hover:bg-yellow-700 text-white'
            }`}
          >
            Upgrade Plan
          </button>
        )}
      </div>
    </div>
  );
}
